/**
 * MandatoryUpdateScreen - Blocking overlay for mandatory updates
 * Displayed when the app version is below minimum required version
 */

import { RefreshCw } from 'lucide-react';

interface MandatoryUpdateScreenProps {
  onUpdate: () => void;
  isUpdating?: boolean;
}

export function MandatoryUpdateScreen({ onUpdate, isUpdating = false }: MandatoryUpdateScreenProps) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50 dark:from-slate-900 dark:to-slate-800">
      <div className="mx-4 max-w-sm text-center">
        {/* Icon */}
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/30">
          <RefreshCw className="h-10 w-10 text-rose-600 dark:text-rose-400" />
        </div>

        {/* Title */}
        <h1 className="mb-3 text-2xl font-semibold text-slate-900 dark:text-white">
          Update Required
        </h1>

        {/* Description */}
        <p className="mb-6 text-slate-600 dark:text-slate-400">
          A new version of Postpartner is available with important improvements.
          Please update to continue using the app.
        </p>

        {/* Update Button */}
        <button
          onClick={onUpdate}
          disabled={isUpdating}
          className="inline-flex w-full items-center justify-center gap-2 rounded-button bg-rose-500 px-6 py-3 font-medium text-white shadow-lg motion-safe:transition-all hover:bg-rose-600 active:scale-[0.98] disabled:opacity-50"
        >
          {isUpdating ? (
            <>
              <RefreshCw className="h-5 w-5 motion-safe:animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <RefreshCw className="h-5 w-5" />
              Update Now
            </>
          )}
        </button>

        {/* Subtle note */}
        <p className="mt-4 text-xs text-slate-500 dark:text-slate-500">
          This will only take a moment
        </p>
      </div>
    </div>
  );
}

